import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import ListingTableWithCellInputs from '@components/table/with-cell-inputs/ListingTableWithCellInputs';
import { IListingData } from '@models/listing';
import { headers } from './content';
import { getCancellationAmendmentDetails } from '../../queries';
import { mapToCancellationAmendmentDetails } from './utils';
import { ActionTarget, IEnhancedMenuItem } from '@components/table/types';
import PolicyPlanCoverDetailsDrawer from '@modules/production/policy/page/tabs/amendments-tab/covers-details-drawer/PolicyPlanCoverDetailsDrawer';

interface IPolicyPlanCoversListingWidgetProps {
  selectedAmendmentID: string;
  onDataRefresh: () => void;
  allowEndorsement: boolean;
  lineExternalCode: string;
  status: string;
}

const PolicyPlanCoversListingWidget: React.FC<
  IPolicyPlanCoversListingWidgetProps
> = ({
  selectedAmendmentID,
  onDataRefresh,
  allowEndorsement,
  lineExternalCode,
  status,
}) => {
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState<Record<string, any>>({});

  const [getCancellationAmendmentDetailsLazy, { data, loading }] = useLazyQuery(
    getCancellationAmendmentDetails
  );

  const overrideAllowedLines = [
    '4',
    '50',
    '51',
    '43',
    '12',
    '3',
    '33',
    '20',
    '5',
    '9',
    '10',
    '35',
    '13',
    '21',
    '11',
    '15',
    '16',
    '25',
    '27',
    '29',
    '30',
    '31',
    '37',
    '38',
    '39',
    '42',
    '46',
    '2',
    '23',
    '26',
    '8',
  ];

  useEffect(() => {
    const initialize = () => {
      if (!data) {
        // Trigger the lazy query when no data is available.
        getCancellationAmendmentDetailsLazy({
          variables: { selectedAmendmentID },
        });
      } else {
        // When data is available, map it and update the table state.
        const listingData = mapToCancellationAmendmentDetails(data);
        setTableData(listingData);
      }
    };

    initialize();
  }, [selectedAmendmentID, data, getCancellationAmendmentDetailsLazy]);

  const dynamicRowMessage = (row: any): string[] => {
    return row.columns.isOverridden ? ['Overridden values'] : [];
  };

  const handleOverrideClick = (selectedKeys: string[]) => {
    if (selectedKeys.length > 0) {
      const rowKey = selectedKeys[0];
      const rowData = tableData.pagedItems[rowKey];
      setDrawerData(rowData || {});
      setDrawerOpen(true);
    }
  };

  const actions: IEnhancedMenuItem[] = [
    {
      name: 'override',
      title: 'Override',
      onClick: handleOverrideClick,
      isPrimary: true,
      target: ActionTarget.Entity,
      hidden:
        !allowEndorsement || !overrideAllowedLines.includes(lineExternalCode),
      disabled: ['COMPLETED', 'CANCELLED'].includes(status),
      conditionalDisable: (payload, selectedItems) => {
        return !selectedItems[0].IsEditable;
      },
    },
  ];

  return (
    <div style={{ width: '900px' }}>
      <ListingTableWithCellInputs
        name="policyPlanCovers"
        inlineTitle="Policy Plan Covers"
        headers={headers}
        data={tableData}
        loader={loading}
        disableSelection={false}
        inline
        dynamicRowMessage={dynamicRowMessage}
        handleUpdate={() => {}}
        actions={actions}
      />
      {drawerOpen && (
        <PolicyPlanCoverDetailsDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          data={drawerData}
          onSuccess={onDataRefresh}
        />
      )}
    </div>
  );
};

export default PolicyPlanCoversListingWidget;
